import { useEffect, useRef, useState } from 'react';
import { Modal, Button, Space, Select, Input } from '@bancobarigui/barihaus';
import { Progress, Image, List, Avatar, message, Tooltip, Badge } from 'antd';
import {
  VideoCameraOutlined,
  AudioOutlined,
  AudioMutedOutlined,
  VideoCameraAddOutlined,
  PhoneOutlined,
  CameraOutlined,
  DeleteOutlined,
  SendOutlined,
  MessageOutlined,
  UserOutlined,
  SwapOutlined
} from '@ant-design/icons';
import { VideoSession, videoSessionCaptureService, VideoSessionCapture, videoSessionChatService, VideoSessionChatMessage } from '@/services/videoSession.service';
import dayjs from 'dayjs';
import { S3Image } from '@/components/S3Image';
import DailyIframe from '@daily-co/daily-js';
import { tokenManager } from '@/utils/tokenManager';

interface VideoRoomProps {
  open: boolean;
  videoSession: VideoSession | null;
  onClose: () => void;
  onLeave?: () => void;
}

let globalDailyInstance: any = null;
let globalDailyRoomUrl: string | null = null;
let globalSessionId: string | null = null;

export function VideoRoom({ open, videoSession, onClose, onLeave }: VideoRoomProps) {
  const dailyContainerRef = useRef<HTMLDivElement>(null);
  const dailyCallRef = useRef<any>(null);
  const isInitializingRef = useRef<boolean>(false);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const [localVideoTrack, setLocalVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [remoteVideoTrack, setRemoteVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [remoteAudioTrack, setRemoteAudioTrack] = useState<MediaStreamTrack | null>(null);
  const [hasRemoteParticipant, setHasRemoteParticipant] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [selectedCamera, setSelectedCamera] = useState<string>('user');
  const [availableDevices, setAvailableDevices] = useState<MediaDeviceInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [captures, setCaptures] = useState<VideoSessionCapture[]>([]);
  const [uploadingCaptures, setUploadingCaptures] = useState<Set<string>>(new Set());
  const [uploadProgress, setUploadProgress] = useState<Map<string, number>>(new Map());
  const [chatMessages, setChatMessages] = useState<VideoSessionChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const isChatOpenRef = useRef(false);

  useEffect(() => {
    let loadingTimeout: NodeJS.Timeout | null = null;

    const initializeDaily = async () => {
      if (!open || !videoSession) return;

      if (isInitializingRef.current) {
        return;
      }

      const roomUrl = videoSession.uniqueLink;
      const sessionId = videoSession.id;

      if (!roomUrl) {
        setIsLoading(false);
        return;
      }

      isInitializingRef.current = true;

      try {
        if (globalDailyInstance && (globalSessionId !== sessionId || globalDailyRoomUrl !== roomUrl)) {
          try {
            await globalDailyInstance.destroy();
          } catch {
          }
          globalDailyInstance = null;
          globalDailyRoomUrl = null;
          globalSessionId = null;
        }

        if (globalDailyInstance && globalSessionId === sessionId && globalDailyRoomUrl === roomUrl) {
          dailyCallRef.current = globalDailyInstance;
          setIsLoading(false);
          isInitializingRef.current = false;
          return;
        }


        const user = tokenManager.getUser();
        const userName = user?.name || user?.email || 'Participante';

        // Destruir instância anterior se existir (StrictMode/HMR pode causar dupla chamada)
        const instanceToDestroy = globalDailyInstance || dailyCallRef.current;
        if (instanceToDestroy) {
          try {
            await instanceToDestroy.destroy();
          } catch {
          }
          dailyCallRef.current = null;
          globalDailyInstance = null;
          // Aguardar o Daily liberar a instância interna
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        let callObject;
        try {
          callObject = DailyIframe.createCallObject({
            url: roomUrl,
            userName: userName,
            subscribeToTracksAutomatically: true,
            dailyConfig: {
              userMediaAudioConstraints: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
              },
            },
          });
        } catch (createError: any) {
          // Se ainda falhar por duplicata, tentar destruir via API estática e recriar
          if (createError.message?.includes('Duplicate')) {
            try {
              // @ts-ignore - getCallInstance existe mas não está tipado
              const existing = DailyIframe.getCallInstance?.();
              if (existing) await existing.destroy();
            } catch {}
            await new Promise(resolve => setTimeout(resolve, 500));
            callObject = DailyIframe.createCallObject({
              url: roomUrl,
              userName: userName,
              subscribeToTracksAutomatically: true,
              dailyConfig: {
                userMediaAudioConstraints: {
                  echoCancellation: true,
                  noiseSuppression: true,
                  autoGainControl: true,
                },
              },
            });
          } else {
            throw createError;
          }
        }

        dailyCallRef.current = callObject;
        globalDailyInstance = callObject;
        globalDailyRoomUrl = roomUrl;
        globalSessionId = sessionId;

        loadingTimeout = setTimeout(() => {
          setIsLoading(false);
        }, 5000);

        callObject.on('joined-meeting', () => {
          if (loadingTimeout) clearTimeout(loadingTimeout);
          setIsLoading(false);
          const local = callObject.participants().local;
          const vTrack = local?.tracks?.video?.persistentTrack || local?.tracks?.video?.track;
          if (vTrack) {
            setLocalVideoTrack(vTrack);
          }
        });

        callObject.on('error', (error: any) => {
          message.error('Erro ao conectar: ' + (error?.errorMsg || error?.error || 'Erro desconhecido'));
          setIsLoading(false);
        });

        callObject.on('track-started', (event: any) => {
          if (!event?.participant || !event?.track) return;
          const { participant, track } = event;
          if (participant.local) {
            if (track.kind === 'video') {
              setLocalVideoTrack(track);
            }
          } else {
            if (track.kind === 'video') {
              setRemoteVideoTrack(track);
            }
            if (track.kind === 'audio') {
              setRemoteAudioTrack(track);
            }
            setHasRemoteParticipant(true);
          }
        });

        callObject.on('track-stopped', (event: any) => {
          if (!event?.participant) return;
          const { participant, track } = event;
          if (participant.local) {
            if (track?.kind === 'video') setLocalVideoTrack(null);
          } else {
            if (track?.kind === 'video') setRemoteVideoTrack(null);
            if (track?.kind === 'audio') setRemoteAudioTrack(null);
          }
        });

        callObject.on('participant-joined', (event: any) => {
          if (event?.participant && !event.participant.local) {
            setHasRemoteParticipant(true);
            const vTrack = event.participant.tracks?.video?.persistentTrack;
            const aTrack = event.participant.tracks?.audio?.persistentTrack;
            if (vTrack) setRemoteVideoTrack(vTrack);
            if (aTrack) setRemoteAudioTrack(aTrack);
          }
        });

        callObject.on('participant-left', (event: any) => {
          if (event?.participant && !event.participant.local) {
            setRemoteVideoTrack(null);
            setRemoteAudioTrack(null);
            setHasRemoteParticipant(false);
          }
        });

        callObject.on('participant-updated', (event: any) => {
          if (event?.participant?.local) {
            setIsMuted(!event.participant.audio);
            setIsVideoEnabled(event.participant.video);
          }
        });

        callObject.on('app-message', async (event: any) => {
          if (event?.data?.type === 'chat' && event?.fromId !== 'local') {
            const messageText = event.data.message;
            const senderName = event.data.senderName || 'Participante';

            try {
              const newMessage = await videoSessionChatService.sendMessage(videoSession!.id, {
                message: messageText,
                messageType: 0,
                senderName: senderName
              });

              setChatMessages(prev => {
                const isDuplicate = prev.some(m =>
                  m.message === newMessage.message &&
                  m.senderName === newMessage.senderName &&
                  Math.abs(new Date(m.sentAt).getTime() - new Date(newMessage.sentAt).getTime()) < 2000
                );
                if (isDuplicate) return prev;
                if (!isChatOpenRef.current) setUnreadCount(c => c + 1);
                return [...prev, newMessage];
              });
            } catch {
            }
          }
        });

        await callObject.join({ startVideoOff: true, startAudioOff: true });
        // Esperar a conexão SFU estabilizar antes de ligar câmera
        await new Promise(resolve => setTimeout(resolve, 1000));
        callObject.setLocalVideo(true);
        callObject.setLocalAudio(true);
      } catch (err) {
        console.error('[VIDEOROOM] ERROR durante inicialização:', err);
        setIsLoading(false);
      } finally {
        isInitializingRef.current = false;
      }
    };

    if (open && videoSession) {
      // Aguardar o Modal renderizar completamente antes de inicializar o Daily
      const timer = setTimeout(() => {
        initializeDaily();
      }, 500);
      return () => clearTimeout(timer);
    }

    return () => {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
      isInitializingRef.current = false;
    };
  }, [open, videoSession]);

  useEffect(() => {
    const cleanupDaily = async () => {
      if (!open && globalDailyInstance) {
        try {
          await globalDailyInstance.destroy();
        } catch {
        }
        globalDailyInstance = null;
        globalDailyRoomUrl = null;
        globalSessionId = null;
        dailyCallRef.current = null;
      }
    };

    if (!open) {
      // Reset do flag de inicialização quando modal fecha
      // Isso permite que a próxima abertura inicie corretamente
      isInitializingRef.current = false;
      // Limpar tracks
      setLocalVideoTrack(null);
      setRemoteVideoTrack(null);
      setRemoteAudioTrack(null);
      setHasRemoteParticipant(false);
      cleanupDaily();
    }
  }, [open]);

  useEffect(() => {
    if (open && videoSession) {
      loadMediaDevices();
      loadCaptures();
      loadMessages();
      const interval = setInterval(() => {
        loadMessages();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [open, videoSession]);

  // useEffect(() => {
  //   messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  // }, [chatMessages]);

  // Conectar tracks de vídeo/áudio aos elementos HTML
  useEffect(() => {
    if (localVideoRef.current) {
      if (localVideoTrack) {
        localVideoRef.current.srcObject = new MediaStream([localVideoTrack]);
      } else {
        localVideoRef.current.srcObject = null;
      }
    }
  }, [localVideoTrack]);

  useEffect(() => {
    if (remoteVideoRef.current) {
      if (remoteVideoTrack) {
        remoteVideoRef.current.srcObject = new MediaStream([remoteVideoTrack]);
      } else {
        remoteVideoRef.current.srcObject = null;
      }
    }
  }, [remoteVideoTrack]);

  useEffect(() => {
    if (remoteAudioRef.current) {
      if (remoteAudioTrack) {
        remoteAudioRef.current.srcObject = new MediaStream([remoteAudioTrack]);
      } else {
        remoteAudioRef.current.srcObject = null;
      }
    }
  }, [remoteAudioTrack]);

  useEffect(() => {
    const checkIfMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(isMobileDevice || isTouchDevice);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const loadMediaDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setAvailableDevices(videoDevices);
    } catch {
    }
  };


  const handleToggleMute = () => {
    if (dailyCallRef.current) {
      const newMuted = !isMuted;
      dailyCallRef.current.setLocalAudio(!newMuted);
      setIsMuted(newMuted);
    }
  };

  const handleToggleVideo = () => {
    if (dailyCallRef.current) {
      const newEnabled = !isVideoEnabled;
      dailyCallRef.current.setLocalVideo(newEnabled);
      setIsVideoEnabled(newEnabled);
    }
  };

  const handleChangeCamera = async (value: string) => {
    setSelectedCamera(value);
    if (dailyCallRef.current) {
      if (value !== 'user' && value !== 'environment') {
        try {
          await dailyCallRef.current.setInputDevicesAsync({
            videoDeviceId: value
          });
        } catch {
        }
      }
    }
  };

  const handleFlipCamera = async () => {
    if (!dailyCallRef.current) return;

    try {
      const { device } = await dailyCallRef.current.cycleCamera();

      if (!device) {
        message.warning('Apenas uma câmera disponível neste dispositivo');
        return;
      }

      const local = dailyCallRef.current.participants().local;
      const localTrack =
        local?.tracks?.video?.persistentTrack || local?.tracks?.video?.track;
      const settings = localTrack?.getSettings?.();
      const labelLc = (device.label || '').toLowerCase();
      const isBack =
        settings?.facingMode === 'environment' ||
        /back|rear|traseir|environment/.test(labelLc);
      const newFacing = isBack ? 'environment' : 'user';

      setSelectedCamera(newFacing);
      message.success(`Câmera alterada para ${newFacing === 'user' ? 'frontal' : 'traseira'}`);
    } catch {
      message.error('Erro ao inverter câmera');
    }
  };

  const loadCaptures = async () => {
    if (!videoSession) return;

    try {
      const capturesList = await videoSessionCaptureService.getCapturesByVideoSessionId(videoSession.id);
      setCaptures(capturesList);
    } catch {
    }
  };

  const loadMessages = async () => {
    if (!videoSession) return;

    try {
      setLoadingMessages(true);
      const messages = await videoSessionChatService.getMessages(videoSession.id);
      setChatMessages(messages);
    } catch {
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!videoSession || !chatInput.trim() || sendingMessage) return;

    const messageText = chatInput.trim();

    try {
      setSendingMessage(true);

      const newMessage = await videoSessionChatService.sendMessage(videoSession.id, {
        message: messageText,
        messageType: 0
      });

      setChatMessages(prev => [...prev, newMessage]);

      if (dailyCallRef.current) {
        try {
          const user = tokenManager.getUser();
          const senderName = user?.name || user?.email || 'Inspetor';

          await dailyCallRef.current.sendAppMessage({
            type: 'chat',
            message: messageText,
            senderName: senderName,
            timestamp: new Date().toISOString()
          }, '*');
        } catch {
        }
      }

      setChatInput('');
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error: any) {
      message.error('Erro ao enviar mensagem: ' + (error.response?.data?.message || error.message));
    } finally {
      setSendingMessage(false);
    }
  };

  const formatMessageTime = (dateString: string) => {
    return dayjs(dateString).format('HH:mm');
  };

  const handleCaptureScreen = async () => {
    if (!videoSession) {
      message.error('Chamada de vídeo não está ativa');
      return;
    }

    if (!remoteVideoTrack || remoteVideoTrack.readyState !== 'live') {
      message.error('O participante não está com a câmera ligada');
      return;
    }

    try {
      message.loading({ content: 'Capturando câmera do participante...', key: 'capture-processing', duration: 0 });

      // Usar o track remoto diretamente (disponível via createCallObject)
      const stream = new MediaStream([remoteVideoTrack]);
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;

      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => {
          video.play().then(() => setTimeout(() => resolve(), 200)).catch(reject);
        };
        video.onerror = () => reject(new Error('Erro ao carregar vídeo'));
        setTimeout(() => reject(new Error('Timeout ao carregar vídeo')), 5000);
      });

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        message.error({ content: 'Erro ao criar contexto do canvas', key: 'capture-processing' });
        return;
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(async (blob) => {
        if (blob) {
          const fileName = `capture-${Date.now()}.jpg`;
          const file = new File([blob], fileName, { type: 'image/jpeg' });
          try {
            await uploadCapture(file);
            message.success({ content: 'Câmera do participante capturada com sucesso!', key: 'capture-processing' });
          } catch (uploadError: any) {
            message.error({
              content: 'Erro ao fazer upload: ' + (uploadError.message || 'Erro desconhecido'),
              key: 'capture-processing'
            });
          }
        } else {
          message.error({ content: 'Erro ao processar imagem', key: 'capture-processing' });
        }
      }, 'image/jpeg', 0.95);

    } catch (error: any) {
      message.error({ content: 'Erro ao capturar: ' + (error.message || 'Erro desconhecido'), key: 'capture-processing' });
    }
  };

  const uploadCapture = async (file: File) => {
    if (!videoSession) return;

    const captureId = `temp-${Date.now()}`;
    setUploadingCaptures(prev => new Set(prev).add(captureId));
    setUploadProgress(prev => new Map(prev).set(captureId, 0));

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const newMap = new Map(prev);
          const current = newMap.get(captureId) || 0;
          if (current < 90) {
            newMap.set(captureId, current + 10);
            return newMap;
          }
          return newMap;
        });
      }, 200);

      const capture = await videoSessionCaptureService.uploadCapture(videoSession.id, file);

      clearInterval(progressInterval);
      setUploadProgress(prev => {
        const newMap = new Map(prev);
        newMap.set(captureId, 100);
        return newMap;
      });

      setCaptures(prev => [capture, ...prev]);
      message.success('Captura enviada com sucesso!');

      setTimeout(() => {
        setUploadingCaptures(prev => {
          const newSet = new Set(prev);
          newSet.delete(captureId);
          return newSet;
        });
        setUploadProgress(prev => {
          const newMap = new Map(prev);
          newMap.delete(captureId);
          return newMap;
        });
      }, 1000);
    } catch (error: any) {
      message.error('Erro ao enviar captura: ' + (error.response?.data?.message || error.message));
      setUploadingCaptures(prev => {
        const newSet = new Set(prev);
        newSet.delete(captureId);
        return newSet;
      });
      setUploadProgress(prev => {
        const newMap = new Map(prev);
        newMap.delete(captureId);
        return newMap;
      });
    }
  };

  const handleDeleteCapture = async (captureId: string) => {
    if (!videoSession) return;

    try {
      await videoSessionCaptureService.deleteCapture(videoSession.id, captureId);
      setCaptures(prev => prev.filter(c => c.id !== captureId));
      message.success('Captura deletada com sucesso!');
    } catch (error: any) {
      message.error('Erro ao deletar captura: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleLeave = async () => {
    if (dailyCallRef.current) {
      try {
        await dailyCallRef.current.leave();
        await dailyCallRef.current.destroy();
      } catch {
      }
      // Limpar referências globais
      globalDailyInstance = null;
      globalDailyRoomUrl = null;
      globalSessionId = null;
      dailyCallRef.current = null;
    }
    onLeave?.();
    onClose();
  };

  if (!videoSession) {
    return null;
  }

  return (
    <Modal
      open={open}
      onCancel={handleLeave}
      footer={null}
      maskClosable={false}
      width={isMobile ? '100%' : '90%'}
      style={{ maxWidth: isMobile ? '100%' : '1200px', top: isMobile ? 0 : undefined, padding: isMobile ? 0 : undefined }}
      closeIcon={null}
      className="video-room-modal"
    >
      <div className="video-room-container">
        {/* Header compacto */}
        <div className="flex justify-between items-center mb-3 pb-3 border-b">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <h3 className="text-lg font-semibold mb-0 flex-shrink-0">{videoSession.title || 'Videoconferência'}</h3>
            <span className="text-xs text-gray-400 truncate hidden md:inline" title={videoSession.guestLink || videoSession.uniqueLink}>
              Link: <a href={videoSession.guestLink || videoSession.uniqueLink} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{videoSession.guestLink || videoSession.uniqueLink}</a>
            </span>
            {!isMobile && availableDevices.length > 1 && (
              <Select
                value={selectedCamera}
                onChange={handleChangeCamera}
                style={{ width: 150 }}
                suffixIcon={<VideoCameraAddOutlined />}
                options={[
                  { value: 'user', label: 'Câmera Frontal', disabled: false },
                  { value: 'environment', label: 'Câmera Traseira', disabled: false },
                  ...availableDevices.map((device, index) => ({
                    value: device.deviceId,
                    label: device.label || `Câmera ${index + 1}`,
                    disabled: false
                  }))
                ]}
              />
            )}
          </div>
        </div>

        {/* Container principal: Vídeo + Controles + Chat */}
        <div className={`flex gap-3 ${isMobile ? 'flex-col' : ''}`} style={{ height: isMobile ? 'auto' : '560px' }}>
          {/* Container de Vídeo */}
          <div className="flex-1" style={{
            position: 'relative',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #d9d9d9',
            background: '#1a1a2e',
            height: isMobile ? '300px' : '100%',
          }}>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                <div className="text-center">
                  <VideoCameraOutlined className="text-4xl text-gray-400 mb-4" />
                  <p className="text-gray-500">Carregando sala de vídeo...</p>
                </div>
              </div>
            )}

            {/* Vídeo remoto (grande) */}
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                background: '#1a1a2e',
              }}
            />
            {/* Áudio remoto */}
            <audio ref={remoteAudioRef} autoPlay playsInline />

            {!hasRemoteParticipant && !isLoading && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                color: '#999',
              }}>
                <UserOutlined style={{ fontSize: 48, marginBottom: 16, display: 'block' }} />
                <p>Aguardando participante...</p>
              </div>
            )}

            {/* Vídeo local (pip pequeno no canto) */}
            <div style={{
              position: 'absolute',
              bottom: isMobile ? 8 : 16,
              right: isMobile ? 8 : 16,
              width: isMobile ? 120 : 180,
              height: isMobile ? 90 : 135,
              borderRadius: 8,
              overflow: 'hidden',
              border: '2px solid rgba(255,255,255,0.3)',
              background: '#000',
              zIndex: 5,
            }}>
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: selectedCamera === 'user' ? 'scaleX(-1)' : 'none',
                }}
              />
              {!isVideoEnabled && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#333',
                  color: '#999',
                }}>
                  <VideoCameraOutlined style={{ fontSize: 24 }} />
                </div>
              )}
            </div>

            {/* Container ref oculto (necessário para captureViaScreenShare) */}
            <div ref={dailyContainerRef} style={{ width: 0, height: 0, overflow: 'hidden', position: 'absolute' }} />
          </div>

          {/* Controles verticais */}
          <div className="flex flex-col gap-2 justify-center items-center" style={{ width: isMobile ? '100%' : 52, flexDirection: isMobile ? 'row' : 'column', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
            <Tooltip title={isMuted ? 'Ligar Microfone' : 'Desligar Microfone'} placement={isMobile ? 'top' : 'left'}>
              <Button
                shape="circle"
                size="large"
                type={isMuted ? 'default' : 'primary'}
                danger={isMuted}
                icon={isMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
                onClick={handleToggleMute}
              />
            </Tooltip>
            <Tooltip title={isVideoEnabled ? 'Desligar Câmera' : 'Ligar Câmera'} placement={isMobile ? 'top' : 'left'}>
              <Button
                shape="circle"
                size="large"
                type={isVideoEnabled ? 'primary' : 'default'}
                danger={!isVideoEnabled}
                icon={<VideoCameraOutlined />}
                onClick={handleToggleVideo}
              />
            </Tooltip>
            <Tooltip title="Capturar Tela" placement={isMobile ? 'top' : 'left'}>
              <Button
                shape="circle"
                size="large"
                type="primary"
                icon={<CameraOutlined />}
                onClick={handleCaptureScreen}
              />
            </Tooltip>
            <Tooltip title={isChatOpen ? 'Fechar Chat' : 'Abrir Chat'} placement={isMobile ? 'top' : 'left'}>
              <Badge count={unreadCount} size="small" offset={[-4, 4]}>
                <Button
                  shape="circle"
                  size="large"
                  type={isChatOpen ? 'primary' : 'default'}
                  icon={<MessageOutlined />}
                  onClick={() => {
                    setIsChatOpen(!isChatOpen);
                    isChatOpenRef.current = !isChatOpen;
                    if (!isChatOpen) setUnreadCount(0);
                  }}
                />
              </Badge>
            </Tooltip>
            {isMobile && isVideoEnabled && (
              <Tooltip title="Inverter Câmera" placement="top">
                <Button shape="circle" size="large" icon={<SwapOutlined />} onClick={handleFlipCamera} />
              </Tooltip>
            )}
            <Tooltip title="Sair da Chamada" placement={isMobile ? 'top' : 'left'}>
              <Button
                shape="circle"
                size="large"
                danger
                icon={<PhoneOutlined />}
                onClick={handleLeave}
              />
            </Tooltip>
          </div>

          {/* Painel de Chat (toggle) */}
          {isChatOpen && (
          <div className={`${isMobile ? 'w-full' : 'w-72'} flex flex-col rounded-lg`} style={{ background: '#f0f0f0', border: '1px solid #d9d9d9' }}>
            {/* Header do Chat */}
            <div className="p-3 flex items-center gap-2" style={{ borderBottom: '1px solid #d9d9d9' }}>
              <MessageOutlined style={{ color: '#666' }} />
              <span className="font-semibold" style={{ color: '#333' }}>Chat</span>
              <span className="text-xs ml-auto" style={{ color: '#999' }}>({chatMessages.length})</span>
            </div>

            {/* Lista de Mensagens */}
            <div className="flex-1 overflow-y-auto p-3" style={{ maxHeight: isMobile ? '200px' : '400px', minHeight: isMobile ? '200px' : '400px' }}>
              {loadingMessages && chatMessages.length === 0 ? (
                <div className="text-center py-8" style={{ color: '#999' }}>Carregando mensagens...</div>
              ) : chatMessages.length === 0 ? (
                <div className="text-center py-8" style={{ color: '#999' }}>Nenhuma mensagem ainda</div>
              ) : (
                <List
                  dataSource={chatMessages}
                  renderItem={(msg: VideoSessionChatMessage) => (
                    <List.Item style={{ border: 'none', padding: '8px 0' }}>
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} size="small" />}
                        title={
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium" style={{ color: '#333' }}>{msg.senderName}</span>
                            <span className="text-xs" style={{ color: '#999' }}>
                              {formatMessageTime(msg.sentAt)}
                            </span>
                          </div>
                        }
                        description={
                          <p className="text-sm mt-1 mb-0" style={{ color: '#555' }}>{msg.message}</p>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input de Mensagem */}
            <div className="p-3 flex gap-2" style={{ borderTop: '1px solid #d9d9d9' }}>
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onPressEnter={handleSendMessage}
                placeholder="Digite uma mensagem..."
                disabled={sendingMessage}
                style={{ flex: 1 }}
              />
              <Button
                buttonColor="primary"
                icon={<SendOutlined />}
                onClick={handleSendMessage}
                loading={sendingMessage}
                disabled={!chatInput.trim()}
              >
                Enviar
              </Button>
            </div>
          </div>
          )}
        </div>

        {/* Capturas realizadas */}
        {(captures.length > 0 || uploadingCaptures.size > 0) && (
          <div className="mt-4 pb-4 border-b">
            <h4 className="text-md font-semibold mb-3">Capturas Realizadas ({captures.length})</h4>
            <div className="flex flex-wrap gap-3">
              {/* Capturas em upload primeiro */}
              {Array.from(uploadingCaptures).map((captureId) => (
                <div
                  key={captureId}
                  className="w-36 h-28 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-300"
                >
                  <div className="text-center p-2">
                    <CameraOutlined className="text-2xl text-gray-400 mb-2 block" />
                    <Progress
                      percent={uploadProgress.get(captureId) || 0}
                      size="small"
                      className="w-20"
                    />
                  </div>
                </div>
              ))}
              {/* Capturas já salvas */}
              {captures.map((capture) => (
                <div
                  key={capture.id}
                  style={{
                    position: 'relative',
                    width: 144,
                    height: 112,
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  {capture.fileUrl?.startsWith('docman/') ? (
                    <S3Image
                      documentId={capture.fileUrl}
                      alt={capture.description || capture.fileName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      preview={true}
                      {...(capture.entityId && { entityId: capture.entityId })}
                      {...(capture.documentType && { documentType: capture.documentType })}
                      {...(capture.fileName && { documentName: capture.fileName })}
                    />
                  ) : (
                    <Image
                      src={capture.fileUrl}
                      alt={capture.description || capture.fileName}
                      width={144}
                      height={112}
                      style={{ objectFit: 'cover', display: 'block' }}
                      preview
                    />
                  )}
                  <button
                    type="button"
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      background: 'rgba(255, 77, 79, 0.9)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      padding: '4px 6px',
                      cursor: 'pointer',
                      zIndex: 100,
                      lineHeight: 1,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCapture(capture.id);
                    }}
                  >
                    <DeleteOutlined style={{ fontSize: 14 }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </Modal>
  );
}

